import React from 'react';

import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ResourcesSection = () => {
  const resources = [
  {
    type: "Report",
    title: "2026 Medical Policy Trends Across Payers",
    description: "Comprehensive analysis of coverage policy changes across Medicare, Medicaid, and commercial insurers—and what they mean for providers.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe696005-1767951892574.png",
    imageAlt: "Professional business document with charts and graphs showing medical policy trend analysis on modern desk",
    icon: "FileText",
    readTime: "12 min read",
    category: "Policy Intelligence"
  },
  {
    type: "Case Study",
    title: "How ENT Specialists Improved Coverage Tracking",
    description: "Learn how a multi-location specialty practice streamlined policy monitoring and reduced coverage denials by 35% using PolicyAlerts.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15acfca5c-1767546591065.png",
    imageAlt: "Medical practice analytics dashboard displaying coverage tracking metrics and policy monitoring statistics on computer screen",
    icon: "Award",
    readTime: "8 min read",
    category: "Client Success"
  },
  {
    type: "Guide",
    title: "Medicare Coverage Policy Monitoring Best Practices",
    description: "Essential strategies for tracking and responding to Medicare policy changes that impact coverage decisions and reimbursement.",
    image: "https://images.unsplash.com/photo-1666886573531-48d2e3c2b684",
    imageAlt: "Healthcare professional reviewing Medicare policy documents and coverage guidelines on tablet device in clinical setting",
    icon: "BookOpen",
    readTime: "15 min read",
    category: "Coverage Guide"
  }];


  return (
    <section className="bg-white py-16 md:py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 md:gap-8 mb-12 md:mb-16">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-[#F59E0B]/10 rounded-full mb-4 md:mb-6">
              <Icon name="BookOpen" size={16} color="#F59E0B" />
              <span className="text-xs md:text-sm font-medium text-[#F59E0B]">Resources</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B1B20] mb-3 md:mb-4">
              Policy Intelligence Hub
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl">
              Expert insights, coverage guides, and policy analysis to help you stay ahead of payer changes.
            </p>
          </div>
          <Button
            variant="outline"
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            className="flex-shrink-0">

            View All Resources
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {resources?.map((resource, idx) =>
          <div
            key={idx}
            className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">

              <div className="relative h-48 md:h-56 overflow-hidden">
                <Image
                src={resource?.image}
                alt={resource?.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full">
                  <span className="text-xs font-medium text-[#1B1B20]">{resource?.type}</span>
                </div>
              </div>

              <div className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-3 md:mb-4">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-[#1A73E8]/10 rounded-lg flex items-center justify-center">
                    <Icon name={resource?.icon} size={18} color="#1A73E8" />
                  </div>
                  <span className="text-xs md:text-sm text-[#1A73E8] font-medium">{resource?.category}</span>
                </div>

                <h3 className="text-lg md:text-xl font-bold text-[#1B1B20] mb-2 md:mb-3 line-clamp-2 group-hover:text-[#1A73E8] transition-colors">
                  {resource?.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 line-clamp-3">
                  {resource?.description}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
                    <Icon name="Clock" size={14} />
                    <span>{resource?.readTime}</span>
                  </div>
                  <button className="flex items-center gap-2 text-xs md:text-sm font-medium text-[#1A73E8] group-hover:gap-3 transition-all">
                    Read More
                    <Icon name="ArrowRight" size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 md:mt-16 bg-gradient-to-br from-[#1A73E8]/5 to-[#ED1C24]/5 rounded-2xl p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#1B1B20] mb-3 md:mb-4">
                Stay Informed with Policy Updates
              </h3>
              <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8">
                Get weekly policy updates, coverage analysis, and payer insights delivered to your inbox.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 md:px-5 md:py-3.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#1A73E8] focus:border-transparent text-sm md:text-base" />

                <Button
                  variant="default"
                  size="lg"
                  iconName="Send"
                  iconPosition="right"
                  className="bg-[#1A73E8] hover:bg-[#1A73E8]/90">

                  Subscribe
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 md:gap-6">
              <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm">
                <Icon name="Mail" size={32} className="mb-3 md:mb-4" color="#1A73E8" />
                <p className="text-2xl md:text-3xl font-bold text-[#1B1B20] mb-1">15K+</p>
                <p className="text-xs md:text-sm text-gray-600">Newsletter Subscribers</p>
              </div>
              <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm">
                <Icon name="FileText" size={32} className="mb-3 md:mb-4" color="#10B981" />
                <p className="text-2xl md:text-3xl font-bold text-[#1B1B20] mb-1">200+</p>
                <p className="text-xs md:text-sm text-gray-600">Published Resources</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default ResourcesSection;